import { useEffect } from "react";

import {
  AgentsIcon,
  Box,
  Button,
  CheckIcon,
  Column,
  ExternalLinkIcon,
  Row,
  Text,
} from "@hightouch/htui";
import bgImage from "src/assets/modals/lcm-intro-modal-bg.png";
import { NewFeatureDialog } from "src/components/new-feature-dialog";
import * as analytics from "src/lib/analytics";
import { Link } from "src/router";
import { useShouldShowLcmIntroDialog } from "./use-should-show-lcm-intro-dialog";

const AI_TERMS_URL = `${import.meta.env.VITE_WEBSITE_URL}/ai-addendum`;

const CHECKLIST_ITEMS = [
  "Turn a campaign goal into a full brief and message plan",
  "Generate on-brand email, push, and SMS content, ready to ship",
  "Review everything before it launches through your existing ESP",
];

export const LcmIntroDialog = () => {
  const { shouldShow, setIsDismissed } = useShouldShowLcmIntroDialog();

  const onDismiss = () => {
    setIsDismissed(true);
    analytics.track("LCM Intro Modal Dismissed");
  };

  useEffect(() => {
    if (!shouldShow) return;
    analytics.track("LCM Intro Modal Shown");
  }, [shouldShow]);

  if (!shouldShow) return null;

  return (
    <NewFeatureDialog
      onClose={onDismiss}
      media={{ type: "image", src: bgImage }}
    >
      <NewFeatureDialog.Body>
        <NewFeatureDialog.Heading>
          Welcome to Lifecycle Studio
        </NewFeatureDialog.Heading>
        <Text color="text.secondary">
          From idea to personalized campaigns, in minutes — with agents grounded
          in your customer data and brand context.
        </Text>
        <Column gap={2}>
          {CHECKLIST_ITEMS.map((item) => (
            <Row key={item} gap={2} align="flex-start">
              <Box
                as={CheckIcon}
                color="text.success"
                flexShrink={0}
                mt="2px"
              />
              <Text color="text.secondary">{item}</Text>
            </Row>
          ))}
        </Column>
        <Text color="text.secondary">
          Agents draft, you decide. Nothing is sent to customers without your
          review and approval.
        </Text>
        <Text color="text.secondary">
          Look for the <Box as={AgentsIcon} boxSize={4} mb="-2px" /> icon — it
          marks AI-powered features. Use of Lifecycle Studio is subject to{" "}
          <Link href={AI_TERMS_URL} isExternal>
            Hightouch&apos;s AI terms
            <Box as={ExternalLinkIcon} ml={1} mb="1px" />
          </Link>
          .
        </Text>
      </NewFeatureDialog.Body>
      <NewFeatureDialog.Actions>
        <Button variant="primary" onClick={onDismiss}>
          Get started
        </Button>
      </NewFeatureDialog.Actions>
    </NewFeatureDialog>
  );
};
